import { Mui, MuiIcons } from '@leap4/frontend';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { AXIOS_INSTANCE } from '../../../api/axiosInstance';
import LineChart, { IChartSeries } from '../../common/lineChart';
import PredictionMethodology from '../../common/predictionMethodology';
import ReportHeader from '../common/ReportHeader/ReportHeader';
import { urlService } from '../../routing/urlsService';

const {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} = Mui;
const { Insights: InsightsIcon } = MuiIcons;

interface IAsOfPoint {
  pixelDate: string;
  horizonDays: number;
  expected: number;
  standardError: number | null;
  actual: number | null;
  settled: boolean;
  method: string;
}

interface IAccuracyBucket {
  horizonDays: number;
  method: string;
  samples: number;
  medianAbsErrorPct: number;
  p68AbsErrorPct: number;
  biasPct: number;
}

interface IForecastMethod {
  method: string;
  earliest: string;
  latest: string;
  rows: number;
  current: boolean;
}

const PREDICTED_COLOR = '#64b5f6';
const ACTUAL_COLOR = '#1565c0';

/** Matches the grid and the chart, so one figure does not mean three things across the product. */
const bandColor = (pct: number): string =>
  pct < 15 ? '#4caf50' : pct <= 25 ? '#ffa726' : '#ef5350';

const isReplay = (method: string): boolean => method.endsWith('-backcast');

const versionOf = (method: string): number =>
  Number(method.match(/-v(\d+)(?:-backcast)?$/)?.[1] ?? 0);

/** The model name is long and shared by every tab; the version and whether it ran live is all that differs. */
const methodLabel = (method: string): string =>
  `v${versionOf(method) || '?'} · ${isReplay(method) ? 'replay' : 'live'}`;

/**
 * Superseded, whether it ran live or was replayed. Neither will grow again: the live rows stopped the
 * night the model was replaced, and a replay can only ever be produced by the model now deployed.
 */
const isRetired = (entry: IForecastMethod): boolean => !entry.current;

/** Oldest model first, and within one, the replay before the live rows it predates. */
const methodOrder = (method: string): number => versionOf(method) * 10 + (isReplay(method) ? 0 : 1);

/**
 * The newest model's replay. Live rows accrue one generation a night, so a newly deployed model has a
 * tab with a single day in it — true but useless as a landing page.
 */
const defaultMethod = (list: IForecastMethod[]): string => {
  if (list.length === 0) return '';
  const newest = Math.max(...list.map((m) => versionOf(m.method)));
  const replay = list.find((m) => versionOf(m.method) === newest && isReplay(m.method));
  return (replay ?? [...list].sort((a, b) => methodOrder(b.method) - methodOrder(a.method))[0]).method;
};

/**
 * What the model said, against what happened. Internal only and deliberately so: this is the model's
 * own error, which is a thing to act on rather than a thing to publish.
 */
const PredictionAccuracy = (): JSX.Element => {
  const [campaigns, setCampaigns] = useState<{ id: string; name: string }[]>([]);
  const [idCampaign, setIdCampaign] = useState('');
  const [events, setEvents] = useState<{ key: string; label: string }[]>([]);
  const [event, setEvent] = useState('');
  const [asOfDate, setAsOfDate] = useState(dayjs().subtract(21, 'day').format('YYYY-MM-DD'));

  const [methods, setMethods] = useState<IForecastMethod[]>([]);
  const [method, setMethod] = useState('');

  const [points, setPoints] = useState<IAsOfPoint[]>([]);
  const [buckets, setBuckets] = useState<IAccuracyBucket[]>([]);
  const [settledThrough, setSettledThrough] = useState('');
  const [fittedFrom, setFittedFrom] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    AXIOS_INSTANCE.get('/campaigns', { params: { scope: 'organization' } })
      .then((res) => {
        const list = (res.data.data || []).map((c: any) => ({
          id: c.id_campaign,
          name: c.name || c.id_campaign,
        }));
        setCampaigns(list);
        setIdCampaign((prev) => prev || list[0]?.id || '');
      })
      .catch(() => setCampaigns([]));
  }, []);

  // Only events enrolled in prediction have anything recorded to score.
  useEffect(() => {
    if (!idCampaign) return;
    AXIOS_INSTANCE.get(`/campaigns/${idCampaign}/reporting-config`)
      .then((res) => {
        const list = (res.data?.events || [])
          .filter((e: any) => e?.is_active !== false && e?.is_predicted === true)
          .map((e: any) => ({
            key: String(e.event || '').toLowerCase(),
            label: e.display_label || e.event,
          }));
        setEvents(list);
        setEvent((prev) => (list.some((e: any) => e.key === prev) ? prev : list[0]?.key || ''));
      })
      .catch(() => setEvents([]));
  }, [idCampaign]);

  // One tab per recorded model. Fetched apart from the rest because it decides which model the rest is
  // then read for, and reading more than one at a time sums them into a total no model produced.
  useEffect(() => {
    if (!idCampaign || !event) return;
    AXIOS_INSTANCE.get('/prediction/methods', { params: { id_campaign: idCampaign, event } })
      .then((res) => {
        const list: IForecastMethod[] = (res.data || []).slice().sort(
          (a: IForecastMethod, b: IForecastMethod) => methodOrder(a.method) - methodOrder(b.method),
        );
        setMethods(list);
        setMethod((prev) => (list.some((m) => m.method === prev) ? prev : defaultMethod(list)));
      })
      .catch(() => {
        setMethods([]);
        setMethod('');
      });
  }, [idCampaign, event]);

  const selectedMethod = useMemo(
    () => methods.find((m) => m.method === method) ?? null,
    [methods, method],
  );

  const bounds = useMemo(
    () => (selectedMethod ? { from: selectedMethod.earliest, to: selectedMethod.latest } : null),
    [selectedMethod],
  );

  useEffect(() => {
    if (!idCampaign || !event || !asOfDate || !method) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      AXIOS_INSTANCE.get('/prediction/as-of', {
        params: { id_campaign: idCampaign, event, asOfDate, method },
      }),
      AXIOS_INSTANCE.get('/prediction/accuracy', { params: { id_campaign: idCampaign, event } }),
    ])
      .then(([asOf, accuracy]: any[]) => {
        if (cancelled) return;
        setPoints(asOf.data?.points ?? []);
        setSettledThrough(asOf.data?.settledThrough ?? '');
        setBuckets(accuracy.data?.buckets ?? []);
        setFittedFrom(accuracy.data?.fittedFrom ?? '');
      })
      .catch(() => {
        if (!cancelled) setError('Could not load prediction history for that day.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [idCampaign, event, asOfDate, method]);

  // A model added later was replayed over a shorter range, so switching tabs can leave the picker on a
  // day that model never recorded.
  useEffect(() => {
    if (!bounds) return;
    if (asOfDate < bounds.from) setAsOfDate(bounds.from);
    else if (asOfDate > bounds.to) setAsOfDate(bounds.to);
  }, [bounds, asOfDate]);

  const series = useMemo<IChartSeries[]>(() => {
    if (points.length === 0) return [];
    const labels = points.map((p) => dayjs(p.pixelDate).format('MMM D'));

    return [
      {
        id: 'actual',
        label: 'Actual',
        color: ACTUAL_COLOR,
        scaleKey: 'v',
        points: points.map((p, i) => ({
          label: labels[i],
          tooltipLabel: dayjs(p.pixelDate).format('MMM D, YYYY'),
          // Unsettled actuals are drawn but not claimed: the date is still accruing, so the number is
          // a running total rather than the answer the forecast should be judged against.
          value: p.actual,
          note: p.actual != null && !p.settled ? 'still accruing' : undefined,
        })),
      },
      {
        id: 'predicted',
        label: `Predicted on ${dayjs(asOfDate).format('MMM D')}`,
        color: PREDICTED_COLOR,
        scaleKey: 'v',
        dashed: true,
        points: points.map((p, i) => {
          const pct = p.standardError != null && p.expected > 0
            ? (p.standardError / p.expected) * 100
            : null;
          return {
            label: labels[i],
            tooltipLabel: dayjs(p.pixelDate).format('MMM D, YYYY'),
            value: p.expected,
            note: pct == null ? undefined : `±${pct.toFixed(0)}% · ${p.horizonDays}d ahead`,
            noteColor: pct == null ? undefined : bandColor(pct),
            upper: p.standardError == null ? null : p.expected + p.standardError,
            lower: p.standardError == null ? null : Math.max(0, p.expected - p.standardError),
          };
        }),
      },
    ];
  }, [points, asOfDate]);

  // The accuracy read covers every model at once; the tab decides which one is on screen.
  const shownBuckets = useMemo(
    () => buckets.filter((b) => b.method === method),
    [buckets, method],
  );

  // Only settled dates can be scored, so the headline counts those alone.
  const scored = useMemo(() => points.filter((p) => p.settled && p.actual != null), [points]);
  const headline = useMemo(() => {
    if (scored.length === 0) return null;
    const expected = scored.reduce((sum, p) => sum + p.expected, 0);
    const actual = scored.reduce((sum, p) => sum + (p.actual ?? 0), 0);
    if (actual <= 0) return null;
    return {
      expected,
      actual,
      deviationPct: ((expected - actual) / actual) * 100,
      days: scored.length,
    };
  }, [scored]);

  return (
    <>
      <ReportHeader
        hideTimeNavbar
        breadcrumbs={[
          { link: urlService.predictionAccuracy(), text: 'Prediction Accuracy', icon: <InsightsIcon /> },
        ]}
      />

      <Box sx={{ px: { xs: 2, md: 4 }, pb: 6 }}>
        <Paper elevation={1} sx={{ p: 2, borderRadius: 3, mb: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="pa-campaign">Campaign</InputLabel>
              <Select
                labelId="pa-campaign"
                label="Campaign"
                value={campaigns.length ? idCampaign : ''}
                onChange={(e: any) => setIdCampaign(String(e.target.value))}
              >
                {campaigns.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="pa-event">Event</InputLabel>
              <Select
                labelId="pa-event"
                label="Event"
                value={events.length ? event : ''}
                onChange={(e: any) => setEvent(String(e.target.value))}
              >
                {events.map((e) => (
                  <MenuItem key={e.key} value={e.key}>{e.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="date"
              label="As we saw it on"
              value={asOfDate}
              onChange={(e: any) => e.target.value && setAsOfDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={bounds ? { min: bounds.from, max: bounds.to } : undefined}
              helperText={bounds
                ? `Recorded ${dayjs(bounds.from).format('MMM D')} – ${dayjs(bounds.to).format('MMM D')}`
                : undefined}
            />

            {loading && <CircularProgress size={20} sx={{ mt: 1 }} />}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Dates settle {settledThrough ? `through ${dayjs(settledThrough).format('MMM D')}` : 'once their attribution window closes'} —
            only those can be scored, because a date still accruing has no final answer to be wrong about.
            {fittedFrom && ` Scoring starts at ${dayjs(fittedFrom).format('MMM D')} — forecasts made before that had only a fraction of the history behind them, so they measure where our data begins rather than how good the model is.`}
          </Typography>

          {methods.length > 0 && (
            <>
              <Divider sx={{ mt: 2 }} />
              <Tabs
                value={method}
                onChange={(_: any, next: string) => setMethod(next)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none' } }}
              >
                {methods.map((m) => (
                  <Tab
                    key={m.method}
                    value={m.method}
                    label={isRetired(m) ? `${methodLabel(m.method)} · retired` : methodLabel(m.method)}
                    sx={isRetired(m) ? { opacity: 0.6 } : undefined}
                    title={`${m.method} — ${m.rows.toLocaleString()} rows, ${dayjs(m.earliest).format('MMM D')} to ${dayjs(m.latest).format('MMM D')}`}
                  />
                ))}
              </Tabs>
            </>
          )}
        </Paper>

        {idCampaign && event && (
          <Box sx={{ mb: 3 }}>
            <PredictionMethodology
              idCampaign={idCampaign}
              event={event}
              eventLabel={events.find((e) => e.key === event)?.label}
              method={method}
            />
          </Box>
        )}

        {error && (
          <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }} elevation={1}>
            <Typography variant="body2" color="error">{error}</Typography>
          </Paper>
        )}

        {headline && (
          <Card sx={{ borderRadius: 3, mb: 3 }} elevation={1}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" align="center">
                Over the {headline.days} settled day{headline.days === 1 ? '' : 's'} forecast on{' '}
                {dayjs(asOfDate).format('MMM D, YYYY')}
              </Typography>
              <Stack
                direction="row"
                spacing={5}
                alignItems="center"
                justifyContent="center"
                sx={{ mt: 1 }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    We predicted
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {Math.round(headline.expected).toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    It settled at
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {Math.round(headline.actual).toLocaleString()}
                  </Typography>
                </Box>
                <Chip
                  label={`${headline.deviationPct >= 0 ? '+' : ''}${headline.deviationPct.toFixed(1)}%`}
                  variant="outlined"
                  sx={{
                    fontWeight: 700,
                    color: bandColor(Math.abs(headline.deviationPct)),
                    borderColor: bandColor(Math.abs(headline.deviationPct)),
                  }}
                />
              </Stack>
            </CardContent>
          </Card>
        )}

        {fittedFrom && asOfDate < fittedFrom && (
          <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }} elevation={1}>
            <Typography variant="body2" sx={{ color: '#ffa726' }}>
              This forecast was made before {dayjs(fittedFrom).format('MMM D')}, so it was fitted on a
              fraction of the history the model normally uses. It is excluded from the error table
              below, and the deviation above will read worse than the model deserves.
            </Typography>
          </Paper>
        )}

        {series.length > 0 && (
          <Paper sx={{ p: 2, borderRadius: 3, mb: 3 }} elevation={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Prediction - Actual
            </Typography>
            <LineChart series={series} height={360} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Dashed is the forecast as it stood that morning; solid is the settled outcome. The band is
              the margin of error we published at the time.
            </Typography>
          </Paper>
        )}

        {/* A model recorded today forecasts only dates still accruing, so it has nothing to be scored
            against for weeks. Said plainly, because an empty table reads as a broken page. */}
        {shownBuckets.length === 0 && selectedMethod && !loading && (
          <Paper sx={{ p: 2, borderRadius: 3 }} elevation={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Measured error by horizon — {methodLabel(method)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Nothing to score yet. This model recorded{' '}
              {selectedMethod.earliest === selectedMethod.latest
                ? `one day, ${dayjs(selectedMethod.earliest).format('MMM D')}`
                : `${dayjs(selectedMethod.earliest).format('MMM D')} to ${dayjs(selectedMethod.latest).format('MMM D')}`}
              , and none of the dates it forecast have finished accruing
              {settledThrough ? ` — dates settle through ${dayjs(settledThrough).format('MMM D')}` : ''}.
              The chart above is what it predicted; the error becomes measurable once those dates close.
            </Typography>
          </Paper>
        )}

        {shownBuckets.length > 0 && (
          <Paper sx={{ p: 2, borderRadius: 3 }} elevation={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Measured error by horizon — {methodLabel(method)}, every recorded forecast
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Not scoped to the date above. Horizon accuracy is a property of the model, and a single
              day would give one sample per horizon; this pools every forecast that has since settled.
              Switch tabs to read the same dates through another model.
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Days ahead</TableCell>
                  <TableCell align="right">Samples</TableCell>
                  <TableCell align="right">Median error</TableCell>
                  <TableCell align="right">68th percentile</TableCell>
                  <TableCell align="right">Bias (median)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shownBuckets.map((b) => (
                  <TableRow key={`${b.method}-${b.horizonDays}`}>
                    <TableCell>{b.horizonDays}</TableCell>
                    <TableCell align="right">{b.samples.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: bandColor(b.medianAbsErrorPct), fontWeight: 600 }}>
                      {b.medianAbsErrorPct.toFixed(1)}%
                    </TableCell>
                    <TableCell align="right">{b.p68AbsErrorPct.toFixed(1)}%</TableCell>
                    <TableCell align="right">
                      {b.biasPct >= 0 ? '+' : ''}{b.biasPct.toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Bias is the median signed error: positive means we read high. Where it equals the median error, every forecast at that distance missed the same way. The 68th percentile is the figure the model
              uses internally for its own error, so it is the one to compare against.
            </Typography>
          </Paper>
        )}
      </Box>
    </>
  );
};

export default PredictionAccuracy;
